export type ProductType = 'Gable' | 'Header' | 'LeanTo';
export type PostSize = '6x6' | '8x8' | '10x10';
export type Classification = 'Standard' | 'Class1' | 'Monoslope';
export type WindZone = 'Standard' | 'HighWind';
export type OverhangType = 'Standard' | 'Custom';

export type ProductionStageStatus = 'pending' | 'active' | 'complete' | 'locked';
export type BuildingCompletionStatus = 'not_started' | 'in_progress' | 'complete' | 'no_trusses';

export interface TravelerOrderHeader {
  id: number;
  orderNumber: string;
  customerName: string;
  orderDate: string;
  dueDate: string | null;
  dueDateChangedAt: string | null;
  po: string;
  jobName: string;
  deliveryOption: string;
  location: string;
  notes: string | null;
  signedBy: string | null;
  signedDate: string | null;
}

export interface TravelerBuilding {
  id: number;
  name: string;
  description: string | null;
  width: number | null;
  length: number | null;
  height: number | null;
  baySpacing: number;
  postSize: PostSize;
  geographicLocation: string | null;
  liveLoad: number;
  deadLoad: number;
  windSpeed: number;
  snowLoad: number;
  confirmedLoadRating: boolean;
  isPermanentDwelling: boolean;
  completionStatus: BuildingCompletionStatus;
  gableTrusses: TravelerTruss[];
  headerTrusses: TravelerTruss[];
  leanToTrusses: TravelerTruss[];
}

export interface TravelerTruss {
  id: number;
  buildingId: number;
  productType: ProductType;
  classification: Classification;
  sizeFeet: number;
  sizeInches: number;
  quantity: number;
  roofPitch: string | null;
  overhangType: OverhangType | null;
  overhangSize: number | null;
  carriageBolts: boolean;
  monoslopeTruss: boolean;
  postSize: PostSize;
  postSizeOverride: boolean;
  postSizeOverrideValue: string | null;
  windSpeed: number;
  snowLoad: number;
  notes: string | null;
  measurementInsidePost: number;
  isLocked: boolean;
  specifications: TrussSpecifications | null;
  productionStages: ProductionStage[];
}

export interface TrussSpecifications {
  trussDepth: string;
  overhang: string | null;
  lowerOverhang: string | null;
  upperOverhang: string | null;
  post: string;
  cordMaterial: string | null;
  cordAngle: string | null;
  topCord: string | null;
  bottomCord: string | null;
  drillHoleDim: string | null;
  topPlates: string | null;
  topPlateHeight: string | null;
  endPlates: string;
  angleLength: string | null;
  crossTieLength: string | null;
  carriageBoltsRequired: boolean;
  carriageBoltSize: string | null;
  carriageBoltKits: number | null;
}

export interface ProductionStage {
  id: number;
  trussId: number;
  stageName: string;
  stageOrder: number;
  technicianId: string | null;
  technicianName: string | null;
  date: string | null;
  initials: string | null;
  status: ProductionStageStatus;
  isQcStage: boolean;
  isFinalQc: boolean;
}

export type TaskTrackerStatusApi = 'Pending' | 'InProgress' | 'Complete';

/** A single production stage from the server-side manufacturer stage config (UDOMI-456 / BR-17). */
export interface StageConfigDef {
  stageName: string;
  sequenceOrder: number;
  isQc: boolean;
  isFinalQc: boolean;
}

export interface TaskTrackerResponse {
  id: number;
  trussId: number;
  stageName: string;
  sequenceOrder: number;
  technicianId: number | null;
  technicianName: string | null;
  initials: string | null;
  completedDate: string | null;
  status: TaskTrackerStatusApi;
  createdAt: string;
  updatedAt: string | null;
}

export interface TrussSpecificationResponse {
  trussId: number;
  trussDepth: string;
  overhang: string | null;
  lowerOverhang: string | null;
  upperOverhang: string | null;
  post: string;
  cordMaterial: string | null;
  cordAngle: string | null;
  topCord: string | null;
  bottomCord: string | null;
  drillHoleDim: string | null;
  topPlates: string | null;
  topPlateHeight: string | null;
  endPlates: string;
  angleLength: string | null;
  crossTieLength: string | null;
  carriageBoltsRequired: boolean;
  carriageBoltSize: string | null;
  carriageBoltKits: number | null;
}

export interface TaskTrackerRequest {
  trussId: number;
  stageName: string;
  sequenceOrder: number;
  technicianId: number | null;
  technicianName: string | null;
  initials: string | null;
  completedDate: string | null;
  status: TaskTrackerStatusApi;
}

export function getTrussDescription(truss: TravelerTruss): string {
  const classLabel = truss.classification === 'Class1' ? 'Class 1' : truss.classification;
  const productLabel = truss.productType === 'LeanTo' ? ' Lean-To' : '';
  const inches = truss.sizeInches > 0 ? ` ${truss.sizeInches}"` : '';
  const size = `${truss.sizeFeet}'${inches}`;
  const qty = `x ${truss.quantity}`;
  const overhang = truss.overhangType === 'Custom' ? ' Custom' : '';
  return `${classLabel}${productLabel}, ${size} ${qty}${overhang}`.trim();
}

export function getActualPostWidth(postSize: PostSize | string): number {
  switch (postSize) {
    case '6x6':
      return 5.5;
    case '8x8':
      return 7.5;
    case '10x10':
      return 9.5;
    default:
      return 5.5;
  }
}

export function getWindZone(windSpeed: number): WindZone {
  return windSpeed > 115 ? 'HighWind' : 'Standard';
}

/** UI label derived from ASCE MPH (≤115 Standard, >115 High wind). */
export function formatWindZoneLabel(windSpeed: number): string {
  return getWindZone(windSpeed) === 'HighWind' ? 'High wind' : 'Standard';
}

export function getTrussDepthInches(classification: Classification): number {
  return classification === 'Class1' ? 4 : 18;
}
